import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Search, CheckCircle, XCircle, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { PageSpinner } from '../../components/PageSpinner';
import { Pagination } from '../../components/Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ITEMS_PER_PAGE = 15;

export default function AdminEmpresas() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    filterData();
  }, [companies, searchTerm, filterStatus]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      const response = await axios.get(`${API_URL}/admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
      setFilteredCompanies(response.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let filtered = [...companies];

    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.razon_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.numero_documento?.includes(searchTerm)
      );
    }

    if (filterStatus !== 'all') {
      const isActive = filterStatus === 'active';
      filtered = filtered.filter(c => c.activo === isActive);
    }

    setFilteredCompanies(filtered);
    setCurrentPage(1);
  };

  const handleToggleStatus = async (companyId, currentStatus) => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      await axios.put(`${API_URL}/admin/companies/${companyId}/toggle-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentStatus ? 'Empresa desactivada' : 'Empresa activada');
      fetchCompanies();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center gap-4">
          <button onClick={() => navigate('/admin/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft size={24} /></button>
          <div><h1 className="text-2xl font-bold text-slate-900">Todas las Empresas</h1></div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-8">
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar por razón social o documento..." className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </div>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500">
              <option value="all">Todas</option>
              <option value="active">Activadas</option>
              <option value="inactive">Desactivadas</option>
            </select>
          </div>
        </div>

        {loading ? (
          <PageSpinner />
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Fecha Creación</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Código</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Razón Social</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Documento</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">Estado</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredCompanies.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((company) => (
                  <tr key={company.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-600">{company.created_at ? format(new Date(company.created_at), 'dd/MM/yyyy') : 'N/A'}</td>
                    <td className="px-6 py-4 text-sm font-mono font-semibold text-violet-600">{company.codigo_empresa}</td>
                    <td className="px-6 py-4 text-sm text-slate-900 font-medium">{company.razon_social || company.nombre_fantasia}</td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-mono">{company.numero_documento}</td>
                    <td className="px-6 py-4 text-center">
                      {company.activo ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold"><CheckCircle size={14} /> Activada</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-semibold"><XCircle size={14} /> Desactivada</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button 
                          onClick={() => navigate(`/admin/empresa/${company.id}`)} 
                          className="flex items-center gap-1 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-sm font-semibold transition-colors"
                          data-testid={`btn-ver-detalle-${company.id}`}
                        >
                          <Eye size={16} />
                          Ver detalle
                        </button>
                        <button onClick={() => handleToggleStatus(company.id, company.activo)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${company.activo ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'}`}>
                          {company.activo ? 'Desactivar' : 'Activar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-6 py-4">
              <Pagination currentPage={currentPage} totalPages={Math.ceil(filteredCompanies.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
